import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaPlus, FaPrint, FaTimes, FaTrash, FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import { saveApprovalMemo } from "../services/ApiService";
import { printOnly } from "../services/PrintService";

function ProvisionalInvoiceModal({ open, onClose, products = [], user, onSave }) {
  const todayISO = new Date().toISOString().split("T")[0];

  const emptyDraft = {
    productName: "",
    designNumber: "",
    wRate: 0,
    qty: 1,
  };

  // ==================== FORM STATE ====================
  const [invoiceDate, setInvoiceDate] = useState(todayISO);
  const [customerName, setCustomerName] = useState("");
  const [mobile, setMobile] = useState("");
  const [errors, setErrors] = useState({});
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(emptyDraft);

  // ==================== SAVE/PRINT STATE ====================
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // 'saving', 'success', 'error'
  const [statusMessage, setStatusMessage] = useState("");

  // Reset modal when closed/opened
  useEffect(() => {
    if (!open) return;

    setInvoiceDate(todayISO);
    setCustomerName("");
    setMobile("");
    setErrors({});
    setIsSaving(false);
    setSaveStatus(null);
    setStatusMessage("");
    setItems([]);
    setDraft(emptyDraft);

    const onKeyDown = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, todayISO, onClose]);

  // ==================== UTILITIES ====================
  const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

  const productOptions = useMemo(
    () => [...new Set(products.map((p) => p.productName).filter(Boolean))].sort(),
    [products]
  );

  const companyOptions = [];

  const designOptions = useMemo(() => {
    if (!draft.productName) return [];
    return [
      ...new Set(
        products
          .filter(
            (p) =>
              p.productName === draft.productName
          )
          .map((p) => p.designNumber)
          .filter(Boolean)
      ),
    ].sort();
  }, [products, draft.productName]);

  const draftLineTotal = useMemo(() => {
    const rate = Number(draft.wRate || 0);
    const qty = Number(draft.qty || 0);
    return rate * qty;
  }, [draft.wRate, draft.qty]);

  const grandTotal = useMemo(
    () => items.reduce((sum, it) => sum + Number(it.total || 0), 0),
    [items]
  );

  // ==================== SEARCH SELECT COMPONENT ====================
  function SearchSelect({
    label,
    value,
    options,
    placeholder,
    disabled,
    onChange,
  }) {
    const wrapRef = useRef(null);
    const [openList, setOpenList] = useState(false);
    const [query, setQuery] = useState(value || "");

    useEffect(() => {
      setQuery(value || "");
    }, [value]);

    useEffect(() => {
      const onDocDown = (e) => {
        if (!wrapRef.current) return;
        if (!wrapRef.current.contains(e.target)) {
          setOpenList(false);
          if (query && !options.includes(query)) setQuery(value || "");
        }
      };
      document.addEventListener("mousedown", onDocDown);
      return () => document.removeEventListener("mousedown", onDocDown);
    }, [query, options, value]);

    const filtered = useMemo(() => {
      const q = query.trim().toLowerCase();
      if (!q) return options;
      return options.filter((o) => o.toLowerCase().includes(q));
    }, [options, query]);

    const selectValue = (v) => {
      onChange(v);
      setQuery(v);
      setOpenList(false);
    };

    return (
      <div className="inv-field" ref={wrapRef}>
        <label className="inv-label">{label}</label>

        <div className={`inv-selectWrap ${disabled ? "isDisabled" : ""}`}>
          <input
            value={query}
            disabled={disabled}
            placeholder={placeholder}
            className="inv-input"
            onFocus={() => !disabled && setOpenList(true)}
            onChange={(e) => {
              const val = e.target.value;
              setQuery(val);
              setOpenList(true);

              if (options.includes(val)) {
                onChange(val);
              } else if (value) {
                onChange("");
              }
            }}
            onBlur={() => {
              setTimeout(() => {
                if (query && !options.includes(query)) setQuery(value || "");
                setOpenList(false);
              }, 120);
            }}
          />

          {!disabled && openList && (
            <div className="inv-dropdown" role="listbox">
              {filtered.length === 0 ? (
                <div className="inv-ddEmpty">No results</div>
              ) : (
                filtered.slice(0, 100).map((opt) => (
                  <button
                    type="button"
                    key={opt}
                    className={`inv-ddItem ${opt === value ? "active" : ""}`}
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => selectValue(opt)}
                  >
                    {opt}
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== FORM HANDLERS ====================
  const handleProductChange = (val) => {
    setDraft({
      productName: val,
      designNumber: "",
      wRate: 0,
      qty: 1,
    });
  };

  const handleCompanyChange = (val) => {
  };

  const handleDesignChange = (val) => {
    const found = products.find(
      (p) =>
        p.productName === draft.productName &&
        p.designNumber === val
    );

    setDraft((p) => ({
      ...p,
      designNumber: val,
      wRate: Number(found?.wRate ?? 0),
    }));
  };

  const addItem = () => {
    const qty = Number(draft.qty || 0);
    if (!draft.productName || !draft.designNumber) return;
    if (!qty || qty <= 0) return;

    const id = `${Date.now()}-${Math.random().toString(16).slice(2)}`;

    const item = {
      id,
      productName: draft.productName,
      designNumber: draft.designNumber,
      wRate: Number(draft.wRate || 0),
      qty,
      total: Number(draft.wRate || 0) * qty,
    };

    setItems((prev) => [...prev, item]);
    setDraft(emptyDraft);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.items;
      return next;
    });
  };

  const removeItem = (id) => setItems((prev) => prev.filter((x) => x.id !== id));

  // ==================== VALIDATION ====================
  const validate = () => {
    const next = {};
    const digits = (mobile || "").replace(/\D/g, "");

    if (!customerName.trim()) next.customerName = "Customer name is required";
    if (!digits) next.mobile = "Mobile number is required";
    else if (digits.length !== 10) next.mobile = "Mobile number must be 10 digits";
    if (items.length === 0) next.items = "Add at least 1 item";

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  // ==================== BUILD PAYLOAD ====================
  const buildPayload = () => ({
    type: "APPROVAL_MEMO",
    invoiceDate,
    customerName: customerName.trim(),
    mobile,
    items,
    grandTotal,
    createdOn: new Date().toISOString(),
    createdBy: user?.username || "Admin",
  });

  // ==================== BUTTON HANDLERS ====================

  /**
   * Handle Print Only (no API call)
   */
  const handlePrintOnly = async () => {
    if (!validate()) return;

    const payload = buildPayload();
    console.log("🖨️ Printing only (no API save)...");

    const printSuccess = printOnly(payload);
    
    if (printSuccess) {
      setSaveStatus("success");
      setStatusMessage("✓ Print preview opened!");

      setTimeout(() => {
        setSaveStatus(null);
        setStatusMessage("");
      }, 2000);
    } else {
      setSaveStatus("error");
      setStatusMessage("❌ Could not open print preview (popup blocked?)");

      setTimeout(() => {
        setSaveStatus(null);
        setStatusMessage("");
      }, 3000);
    }
  };

  /**
   * Handle Save & Print (API call + print)
   */
  const handleSaveAndPrint = async () => {
    if (!validate()) return;

    const payload = buildPayload();
    setIsSaving(true);
    setSaveStatus("saving");
    setStatusMessage("💾 Saving to database...");

    try {
      console.log("🚀 Starting Save & Print flow...");
      console.log("📦 Payload:", payload);

      // Step 1: Save to API
      const apiResult = await saveApprovalMemo(payload);

      console.log("📊 API Result:", apiResult);

      if (!apiResult.success) {
        throw new Error(apiResult.message);
      }

      // Step 2: Print
      setSaveStatus("saving");
      setStatusMessage("🖨️ Opening print preview...");

      const printSuccess = printOnly(payload);

      if (!printSuccess) {
        console.warn("Print window blocked but data was saved");
      }

      // Step 3: Success
      setSaveStatus("success");
      setStatusMessage("✅ Saved! Print preview opening...");

      // Call parent callback
      if (typeof onSave === "function") {
        onSave(payload);
      }

      // Close modal after 2 seconds
      setTimeout(() => {
        onClose?.();
      }, 2000);
    } catch (error) {
      console.error("❌ Error:", error);

      setSaveStatus("error");
      setStatusMessage(`❌ Error: ${error.message}`);

      // Keep modal open so user can retry
      setTimeout(() => {
        setSaveStatus(null);
        setStatusMessage("");
      }, 4000);
    } finally {
      setIsSaving(false);
    }
  };

  if (!open) return null;

  const canAdd =
    draft.productName &&
    draft.designNumber &&
    Number(draft.qty || 0) > 0;

  return (
    <div
      className="invOverlay"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
    >
      <div className="invModal" onClick={(e) => e.stopPropagation()}>
        {/* ==================== HEADER ==================== */}
        <div className="invHeader">
          <div className="invHeaderLeft">
            <div className="invLogoCircle">
              <img src="/mtlogo.jpg" alt="logo" />
            </div>
            <div>
              <div className="invTitle">Approval Memo</div>
              <div className="invSub">
                Provisional Invoice • {user?.username || "Admin"}
              </div>
            </div>
          </div>

          <button className="invClose" onClick={onClose} aria-label="Close">
            <FaTimes />
          </button>
        </div>

        {/* ==================== STATUS MESSAGE ==================== */}
        {statusMessage && (
          <div className={`invStatus invStatus-${saveStatus}`}>
            {saveStatus === "saving" && <span className="spinner"></span>}
            {saveStatus === "success" && <FaCheckCircle />}
            {saveStatus === "error" && <FaExclamationCircle />}
            <span>{statusMessage}</span>
          </div>
        )}

        {/* ==================== SCROLL AREA ==================== */}
        <div className="invScrollArea">
          {/* Top Section - Customer Details */}
          <div className="invTop">
            <div className="inv-field">
              <label className="inv-label">Date</label>
              <input
                className="inv-input"
                type="date"
                value={invoiceDate}
                onChange={(e) => setInvoiceDate(e.target.value)}
                disabled={isSaving}
              />
            </div>

            <div className="inv-field">
              <label className="inv-label">Customer Name</label>
              <input
                className="inv-input"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Enter customer name"
                disabled={isSaving}
              />
              {errors.customerName && (
                <div className="invErr">{errors.customerName}</div>
              )}
            </div>

            <div className="inv-field">
              <label className="inv-label">Mobile Number</label>
              <input
                className="inv-input"
                type="tel"
                inputMode="numeric"
                value={mobile}
                onChange={(e) => {
                  const digits = (e.target.value || "")
                    .replace(/\D/g, "")
                    .slice(0, 10);
                  setMobile(digits);
                }}
                placeholder="10 digit mobile number"
                disabled={isSaving}
              />
              {errors.mobile && <div className="invErr">{errors.mobile}</div>}
            </div>
          </div>

          {/* Body Section - Add Items */}
          <div className="invBody">
            <div className="invAddBox">
              <div className="invSectionTitle">Add Item</div>

              <div className="invAddGrid">
                <SearchSelect
                  label="Product Name"
                  value={draft.productName}
                  options={productOptions}
                  placeholder="Search product..."
                  disabled={isSaving}
                  onChange={handleProductChange}
                />



                <SearchSelect
                  label="C Name / Design Number"
                  value={draft.designNumber}
                  options={designOptions}
                  placeholder="Search design..."
                  disabled={!draft.productName || isSaving}
                  onChange={handleDesignChange}
                />

                <div className="inv-field">
                  <label className="inv-label">W Price</label>
                  <input
                    className="inv-input invReadOnly"
                    value={draft.wRate}
                    readOnly
                  />
                </div>

                <div className="inv-field">
                  <label className="inv-label">Qty</label>
                  <input
                    className="inv-input"
                    type="number"
                    min="1"
                    value={draft.qty}
                    disabled={!draft.designNumber || isSaving}
                    onChange={(e) =>
                      setDraft((p) => ({ ...p, qty: e.target.value }))
                    }
                  />
                </div>

                <div className="inv-field">
                  <label className="inv-label">Amount</label>
                  <input
                    className="inv-input invReadOnly invGreen"
                    value={draftLineTotal}
                    readOnly
                  />
                </div>

                <button
                  className="invAddBtn"
                  onClick={addItem}
                  disabled={!canAdd || isSaving}
                >
                  <FaPlus />
                  <span>Add Item</span>
                </button>
              </div>
            </div>

            {errors.items && (
              <div className="invErr invErrBox">{errors.items}</div>
            )}

            {/* Desktop Table View */}
            <div className="invDesktopTable">
              <div className="invTableHead">
                <div>Product</div>
                <div style={{ flex: 1 }}>C Name / Design Number</div>
                <div>W Price</div>
                <div>Qty</div>
                <div>Total</div>
                <div>Remove</div>
              </div>

              {items.length === 0 ? (
                <div className="invEmpty">No items added</div>
              ) : (
                <div className="invTable">
                  {items.map((it, idx) => (
                    <div
                      key={it.id}
                      className={`invRow ${idx % 2 === 0 ? "even" : "odd"}`}
                    >
                      <div className="invStrong">{it.productName}</div>
                      <div className="invStrong">{it.designNumber}</div>
                      <div>{money(it.wRate)}</div>
                      <div>{it.qty}</div>
                      <div className="invGreenStrong">{money(it.total)}</div>
                      <button
                        className="invRemove"
                        onClick={() => removeItem(it.id)}
                        disabled={isSaving}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Cards View */}
            <div className="invMobileCards">
              {items.length === 0 ? (
                <div className="invEmpty">No items added</div>
              ) : (
                items.map((it, index) => (
                  <div key={it.id} className="invCard">
                    <div className="invCardTop">
                      <div className="invCardTopLeft">
                        <div className="invCardIndex">Item #{index + 1}</div>
                        <div className="invCardTitle">{it.productName}</div>
                      </div>
                      <button
                        className="invRemove"
                        onClick={() => removeItem(it.id)}
                        disabled={isSaving}
                      >
                        <FaTrash />
                      </button>
                    </div>

                    <div className="invCardRows">

                      <div className="invCardRow">
                        <span className="invMini">C Name / Design Number</span>
                        <span className="invCardValue invStrong">
                          {it.designNumber}
                        </span>
                      </div>
                      <div className="invCardRow">
                        <span className="invMini">W Price</span>
                        <span className="invCardValue">{money(it.wRate)}</span>
                      </div>
                      <div className="invCardRow">
                        <span className="invMini">Qty</span>
                        <span className="invCardValue">{it.qty}</span>
                      </div>
                      <div className="invCardRow totalRow">
                        <span className="invMini">Total</span>
                        <span className="invCardValue invGreenStrong">
                          {money(it.total)}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* ==================== FOOTER ==================== */}
        <div className="invFooter">
          <div className="invFootLeft">Items: {items.length}</div>

          <div className="invFooterRight">
            <div className="invGrandCompact">
              <span>Grand Total</span>
              <b>{money(grandTotal)}</b>
            </div>

            <button
              className="invBtn invBtnSecondary"
              onClick={handlePrintOnly}
              disabled={isSaving || items.length === 0}
              title="Print without saving"
            >
              <FaPrint /> {isSaving ? "..." : "Print Only"}
            </button>

            <button
              className="invBtn invBtnPrimary"
              onClick={handleSaveAndPrint}
              disabled={isSaving || items.length === 0}
              title="Save to database and print"
            >
              <FaPrint /> {isSaving ? "Saving..." : "Save & Print"}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        /* ==================== STATUS MESSAGE ==================== */
        .invStatus {
          padding: 12px 18px;
          display: flex;
          align-items: center;
          gap: 10px;
          font-weight: 700;
          font-size: 13px;
          border-bottom: 1px solid #e5e7eb;
          animation: slideDown 0.3s ease-out;
        }

        .invStatus-saving {
          background: #fef3c7;
          color: #92400e;
        }

        .invStatus-success {
          background: #ecfdf5;
          color: #047857;
        }

        .invStatus-error {
          background: #fef2f2;
          color: #b91c1c;
        }

        .invStatus .spinner {
          display: inline-block;
          width: 14px;
          height: 14px;
          border: 2px solid currentColor;
          border-top-color: transparent;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        @keyframes slideDown {
          from { transform: translateY(-10px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }

        /* ==================== MODAL STRUCTURE ==================== */
        .invOverlay {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.55);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 3000;
          padding: 12px;
        }

        .invModal {
          width: 100%;
          max-width: 1180px;
          max-height: 95vh;
          background: #fff;
          border-radius: 18px;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          box-shadow: 0 20px 60px rgba(0, 0, 0, 0.22);
        }

        .invHeader {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: #fff;
          padding: 14px 18px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-shrink: 0;
        }

        .invHeaderLeft {
          display: flex;
          align-items: center;
          gap: 12px;
          min-width: 0;
        }

        .invLogoCircle {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: rgba(255, 255, 255, 0.18);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
          border: 2px solid rgba(255, 255, 255, 0.25);
          flex-shrink: 0;
        }

        .invLogoCircle img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .invTitle {
          font-size: 18px;
          font-weight: 900;
          line-height: 1.2;
        }

        .invSub {
          font-size: 12px;
          margin-top: 3px;
          font-weight: 700;
          opacity: 0.95;
        }

        .invClose {
          width: 38px;
          height: 38px;
          border: none;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.18);
          color: #fff;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: background 0.2s;
        }

        .invClose:hover {
          background: rgba(255, 255, 255, 0.25);
        }

        .invScrollArea {
          flex: 1;
          overflow: auto;
          min-height: 0;
        }

        .invTop {
          padding: 16px 18px;
          border-bottom: 1px solid #e5e7eb;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 14px;
        }

        .invBody {
          padding: 16px 18px 16px;
        }

        .invSectionTitle {
          font-size: 14px;
          font-weight: 900;
          color: #111827;
          margin-bottom: 12px;
        }

        /* ==================== FORM FIELDS ==================== */
        .inv-field {
          position: relative;
          min-width: 0;
        }

        .inv-label {
          display: block;
          font-size: 12px;
          font-weight: 900;
          color: #374151;
          margin-bottom: 6px;
        }

        .inv-input {
          width: 100%;
          height: 46px;
          padding: 0 12px;
          border-radius: 12px;
          border: 1px solid #d1d5db;
          outline: none;
          font-size: 14px;
          box-sizing: border-box;
          background: #fff;
          transition: all 0.2s;
        }

        .inv-input:focus {
          border-color: #6366f1;
          box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
        }

        .inv-input:disabled {
          background: #f3f4f6;
          cursor: not-allowed;
        }

        .invReadOnly {
          background: #f3f4f6 !important;
          font-weight: 900;
        }

        .invGreen {
          color: #047857;
          font-weight: 900;
        }

        .invErr {
          margin-top: 6px;
          font-size: 12px;
          font-weight: 800;
          color: #ef4444;
        }

        .invErrBox {
          margin-top: 12px;
          background: #fef2f2;
          border: 1px solid #fecaca;
          padding: 10px 12px;
          border-radius: 12px;
        }

        /* ==================== ADD ITEMS BOX ==================== */
        .invAddBox {
          background: #f9fafb;
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          padding: 14px;
        }

        .invAddGrid {
          display: grid;
          grid-template-columns: 2fr 1.7fr 1fr 0.8fr 1fr 1fr;
          gap: 12px;
          align-items: end;
        }

        .invAddBtn {
          height: 46px;
          border: none;
          border-radius: 12px;
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: #fff;
          font-weight: 900;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          white-space: nowrap;
          transition: all 0.2s;
        }

        .invAddBtn:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 16px rgba(99, 102, 241, 0.3);
        }

        .invAddBtn:disabled {
          background: #d1d5db;
          cursor: not-allowed;
          opacity: 0.6;
        }

        /* ==================== DROPDOWN ==================== */
        .inv-selectWrap {
          position: relative;
        }

        .inv-dropdown {
          position: absolute;
          left: 0;
          right: 0;
          top: calc(100% + 6px);
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 14px;
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.12);
          max-height: 240px;
          overflow: auto;
          z-index: 100;
          padding: 6px;
        }

        .inv-ddItem {
          width: 100%;
          text-align: left;
          border: none;
          background: transparent;
          padding: 10px;
          border-radius: 10px;
          cursor: pointer;
          font-size: 13px;
          font-weight: 700;
          color: #111827;
          transition: background 0.15s;
        }

        .inv-ddItem:hover {
          background: #f3f4f6;
        }

        .inv-ddItem.active {
          background: #eef2ff;
          color: #3730a3;
        }

        .inv-ddEmpty {
          padding: 10px;
          color: #6b7280;
          font-weight: 700;
          font-size: 13px;
        }

        .isDisabled .inv-input {
          background: #f3f4f6;
          cursor: not-allowed;
        }

        /* ==================== TABLE VIEW ==================== */
        .invDesktopTable {
          margin-top: 16px;
        }

        .invTableHead {
          display: grid;
          grid-template-columns: 1.3fr 1fr 0.9fr 0.7fr 1fr 70px;
          gap: 10px;
          padding: 10px 12px;
          color: #6b7280;
          font-size: 12px;
          font-weight: 900;
        }

        .invTable {
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          overflow: hidden;
        }

        .invRow {
          display: grid;
          grid-template-columns: 1.3fr 1fr 0.9fr 0.7fr 1fr 70px;
          gap: 10px;
          padding: 12px;
          align-items: center;
          font-size: 13px;
        }

        .invRow.even {
          background: #fff;
        }

        .invRow.odd {
          background: #fafafa;
        }

        .invStrong {
          font-weight: 900;
          color: #111827;
        }

        .invGreenStrong {
          font-weight: 900;
          color: #047857;
        }

        .invRemove {
          border: 1px solid #fecaca;
          background: #fff;
          color: #ef4444;
          border-radius: 12px;
          padding: 10px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          min-width: 42px;
          min-height: 42px;
          transition: all 0.2s;
        }

        .invRemove:hover:not(:disabled) {
          background: #fef2f2;
          border-color: #ef4444;
        }

        .invRemove:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .invEmpty {
          padding: 18px;
          border: 1px dashed #d1d5db;
          border-radius: 16px;
          text-align: center;
          color: #6b7280;
          font-weight: 800;
          margin-top: 10px;
          background: #fff;
        }

        /* ==================== MOBILE CARDS ==================== */
        .invMobileCards {
          display: none;
          margin-top: 16px;
        }

        .invCard {
          border: 1px solid #e5e7eb;
          border-radius: 16px;
          background: #fff;
          padding: 14px;
          margin-bottom: 12px;
        }

        .invCardTop {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 10px;
          margin-bottom: 10px;
        }

        .invCardTopLeft {
          min-width: 0;
        }

        .invCardIndex {
          font-size: 11px;
          font-weight: 900;
          color: #6366f1;
          margin-bottom: 4px;
        }

        .invCardTitle {
          font-size: 15px;
          font-weight: 900;
          color: #111827;
          line-height: 1.3;
          word-break: break-word;
        }

        .invCardRows {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 8px;
        }

        .invCardRow {
          display: flex;
          justify-content: space-between;
          gap: 12px;
          padding: 10px 12px;
          background: #f9fafb;
          border: 1px solid #eef2f7;
          border-radius: 12px;
        }

        .invCardValue {
          font-size: 13px;
          font-weight: 800;
          color: #111827;
          text-align: right;
          word-break: break-word;
        }

        .totalRow {
          background: #ecfdf5;
          border-color: #a7f3d0;
        }

        .invMini {
          font-size: 11px;
          color: #6b7280;
          font-weight: 900;
          flex-shrink: 0;
        }

        /* ==================== FOOTER ==================== */
        .invFooter {
          border-top: 1px solid #e5e7eb;
          background: #f9fafb;
          padding: 12px 18px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
          flex-shrink: 0;
        }

        .invFootLeft {
          font-size: 12px;
          color: #6b7280;
          font-weight: 900;
        }

        .invFooterRight {
          display: flex;
          align-items: center;
          gap: 10px;
          flex-wrap: wrap;
          margin-left: auto;
        }

        .invGrandCompact {
          display: flex;
          align-items: center;
          gap: 10px;
          background: #fff;
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          padding: 10px 12px;
          font-weight: 900;
          min-height: 44px;
        }

        .invBtn {
          border: none;
          border-radius: 12px;
          padding: 11px 16px;
          font-size: 13px;
          font-weight: 900;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          min-height: 44px;
          transition: all 0.2s;
        }

        .invBtn:hover:not(:disabled) {
          transform: translateY(-2px);
        }

        .invBtn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        .invBtnPrimary {
          background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
          color: #fff;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
        }

        .invBtnPrimary:hover:not(:disabled) {
          box-shadow: 0 6px 16px rgba(99, 102, 241, 0.4);
        }

        .invBtnSecondary {
          background: #fff;
          color: #6366f1;
          border: 2px solid #6366f1;
        }

        .invBtnSecondary:hover:not(:disabled) {
          background: #f0f4ff;
        }

        /* ==================== RESPONSIVE ==================== */
        @media (max-width: 1100px) {
          .invAddGrid {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        @media (max-width: 900px) {
          .invTop {
            grid-template-columns: 1fr 1fr;
          }

          .invAddGrid {
            grid-template-columns: repeat(2, 1fr);
          }
        }

        @media (max-width: 768px) {
          .invOverlay {
            padding: 0;
            align-items: flex-end;
          }

          .invModal {
            max-width: 100%;
            width: 100%;
            height: 100vh;
            max-height: 100vh;
            border-radius: 18px 18px 0 0;
          }

          .invTop {
            grid-template-columns: 1fr;
            gap: 12px;
            padding: 14px;
          }

          .invBody {
            padding: 14px;
          }

          .invAddBox {
            padding: 12px;
          }

          .invAddGrid {
            grid-template-columns: 1fr;
            gap: 12px;
          }

          .invDesktopTable {
            display: none;
          }

          .invMobileCards {
            display: block;
          }

          .invFooter {
            flex-direction: column;
            align-items: stretch;
            padding: 12px 14px;
          }

          .invFootLeft {
            padding-top: 0;
          }

          .invFooterRight {
            width: 100%;
            margin-left: 0;
            flex-direction: column;
            align-items: stretch;
          }

          .invGrandCompact {
            width: 100%;
            justify-content: space-between;
            box-sizing: border-box;
          }

          .invBtn {
            width: 100%;
          }
        }

        @media (max-width: 480px) {
          .invHeader {
            padding: 12px 14px;
          }

          .invLogoCircle {
            width: 42px;
            height: 42px;
          }

          .invTitle {
            font-size: 16px;
          }

          .invSub {
            font-size: 11px;
          }

          .invCard {
            padding: 12px;
          }

          .invCardValue {
            max-width: 60%;
          }
        }
      `}</style>
    </div>
  );
}

export default ProvisionalInvoiceModal;








// import React, { useEffect, useMemo, useRef, useState } from "react";
// import { FaPlus, FaPrint, FaTimes, FaTrash } from "react-icons/fa";

// function ProvisionalInvoiceModal({ open, onClose, products = [], user, onSave }) {
//   const todayISO = new Date().toISOString().split("T")[0];

//   const emptyDraft = {
//     productName: "",
//     companyName: "",
//     designNumber: "",
//     wRate: 0,
//     qty: 1,
//   };

//   const [invoiceDate, setInvoiceDate] = useState(todayISO);
//   const [customerName, setCustomerName] = useState("");
//   const [mobile, setMobile] = useState("");
//   const [errors, setErrors] = useState({});
//   const [isSaving, setIsSaving] = useState(false);
//   const [items, setItems] = useState([]);
//   const [draft, setDraft] = useState(emptyDraft);

//   useEffect(() => {
//     if (!open) return;

//     setInvoiceDate(todayISO);
//     setCustomerName("");
//     setMobile("");
//     setErrors({});
//     setIsSaving(false);
//     setItems([]);
//     setDraft(emptyDraft);

//     const onKeyDown = (e) => e.key === "Escape" && onClose?.();
//     window.addEventListener("keydown", onKeyDown);
//     return () => window.removeEventListener("keydown", onKeyDown);
//   }, [open, todayISO, onClose]);

//   const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

//   const productOptions = useMemo(
//     () => [...new Set(products.map((p) => p.productName).filter(Boolean))].sort(),
//     [products]
//   );

//   const companyOptions = useMemo(() => {
//     if (!draft.productName) return [];
//     return [
//       ...new Set(
//         products
//           .filter((p) => p.productName === draft.productName)
//           .map((p) => p.companyName)
//           .filter(Boolean)
//       ),
//     ].sort();
//   }, [products, draft.productName]);

//   const designOptions = useMemo(() => {
//     if (!draft.productName || !draft.companyName) return [];
//     return [
//       ...new Set(
//         products
//           .filter(
//             (p) =>
//               p.productName === draft.productName &&
//               p.companyName === draft.companyName
//           )
//           .map((p) => p.designNumber)
//           .filter(Boolean)
//       ),
//     ].sort();
//   }, [products, draft.productName, draft.companyName]);

//   const draftLineTotal = useMemo(() => {
//     const rate = Number(draft.wRate || 0);
//     const qty = Number(draft.qty || 0);
//     return rate * qty;
//   }, [draft.wRate, draft.qty]);

//   const grandTotal = useMemo(
//     () => items.reduce((sum, it) => sum + Number(it.total || 0), 0),
//     [items]
//   );

//   function SearchSelect({
//     label,
//     value,
//     options,
//     placeholder,
//     disabled,
//     onChange,
//   }) {
//     const wrapRef = useRef(null);
//     const [openList, setOpenList] = useState(false);
//     const [query, setQuery] = useState(value || "");

//     useEffect(() => {
//       setQuery(value || "");
//     }, [value]);

//     useEffect(() => {
//       const onDocDown = (e) => {
//         if (!wrapRef.current) return;
//         if (!wrapRef.current.contains(e.target)) {
//           setOpenList(false);
//           if (query && !options.includes(query)) setQuery(value || "");
//         }
//       };
//       document.addEventListener("mousedown", onDocDown);
//       return () => document.removeEventListener("mousedown", onDocDown);
//     }, [query, options, value]);

//     const filtered = useMemo(() => {
//       const q = query.trim().toLowerCase();
//       if (!q) return options;
//       return options.filter((o) => o.toLowerCase().includes(q));
//     }, [options, query]);

//     const selectValue = (v) => {
//       onChange(v);
//       setQuery(v);
//       setOpenList(false);
//     };

//     return (
//       <div className="inv-field" ref={wrapRef}>
//         <label className="inv-label">{label}</label>

//         <div className={`inv-selectWrap ${disabled ? "isDisabled" : ""}`}>
//           <input
//             value={query}
//             disabled={disabled}
//             placeholder={placeholder}
//             className="inv-input"
//             onFocus={() => !disabled && setOpenList(true)}
//             onChange={(e) => {
//               const val = e.target.value;
//               setQuery(val);
//               setOpenList(true);

//               if (options.includes(val)) {
//                 onChange(val);
//               } else if (value) {
//                 onChange("");
//               }
//             }}
//             onBlur={() => {
//               setTimeout(() => {
//                 if (query && !options.includes(query)) setQuery(value || "");
//                 setOpenList(false);
//               }, 120);
//             }}
//           />

//           {!disabled && openList && (
//             <div className="inv-dropdown" role="listbox">
//               {filtered.length === 0 ? (
//                 <div className="inv-ddEmpty">No results</div>
//               ) : (
//                 filtered.slice(0, 100).map((opt) => (
//                   <button
//                     type="button"
//                     key={opt}
//                     className={`inv-ddItem ${opt === value ? "active" : ""}`}
//                     onMouseDown={(e) => e.preventDefault()}
//                     onClick={() => selectValue(opt)}
//                   >
//                     {opt}
//                   </button>
//                 ))
//               )}
//             </div>
//           )}
//         </div>
//       </div>
//     );
//   }

//   const handleProductChange = (val) => {
//     setDraft({
//       productName: val,
//       companyName: "",
//       designNumber: "",
//       wRate: 0,
//       qty: 1,
//     });
//   };

//   const handleCompanyChange = (val) => {
//     setDraft((p) => ({
//       ...p,
//       companyName: val,
//       designNumber: "",
//       wRate: 0,
//       qty: 1,
//     }));
//   };

//   const handleDesignChange = (val) => {
//     const found = products.find(
//       (p) =>
//         p.productName === draft.productName &&
//         p.companyName === draft.companyName &&
//         p.designNumber === val
//     );

//     setDraft((p) => ({
//       ...p,
//       designNumber: val,
//       wRate: Number(found?.wRate ?? 0),
//     }));
//   };

//   const addItem = () => {
//     const qty = Number(draft.qty || 0);
//     if (!draft.productName || !draft.companyName || !draft.designNumber) return;
//     if (!qty || qty <= 0) return;

//     const id =
//       window.crypto?.randomUUID?.() ??
//       `${Date.now()}-${Math.random().toString(16).slice(2)}`;

//     const item = {
//       id,
//       productName: draft.productName,
//       companyName: draft.companyName,
//       designNumber: draft.designNumber,
//       wRate: Number(draft.wRate || 0),
//       qty,
//       total: Number(draft.wRate || 0) * qty,
//     };

//     setItems((prev) => [...prev, item]);
//     setDraft(emptyDraft);
//     setErrors((prev) => {
//       const next = { ...prev };
//       delete next.items;
//       return next;
//     });
//   };

//   const removeItem = (id) => setItems((prev) => prev.filter((x) => x.id !== id));

//   const validate = () => {
//     const next = {};
//     const digits = (mobile || "").replace(/\D/g, "");

//     if (!customerName.trim()) next.customerName = "Customer name is required";
//     if (!digits) next.mobile = "Mobile number is required";
//     else if (digits.length !== 10) next.mobile = "Mobile number must be 10 digits";
//     if (items.length === 0) next.items = "Add at least 1 item";

//     setErrors(next);
//     return Object.keys(next).length === 0;
//   };

//   const buildPayload = () => ({
//     type: "APPROVAL_MEMO",
//     invoiceDate,
//     customerName: customerName.trim(),
//     mobile,
//     items,
//     grandTotal,
//     createdOn: new Date().toISOString(),
//     createdBy: user?.username || "Admin",
//   });

//   const printInvoice = (payload) => {
//     const w = window.open("", "_blank", "width=900,height=700");
//     if (!w) return;

//     const rows = payload.items
//       .map(
//         (it, i) => `
//         <tr>
//           <td>${i + 1}</td>
//           <td>${it.productName}</td>
//           <td>${it.companyName}</td>
//           <td><b>${it.designNumber}</b></td>
//           <td>${it.qty}</td>
//           <td>₹${Number(it.wRate).toLocaleString("en-IN")}</td>
//           <td><b>₹${Number(it.total).toLocaleString("en-IN")}</b></td>
//         </tr>
//       `
//       )
//       .join("");

//     w.document.write(`
//       <html>
//         <head>
//           <title>Approval Memo</title>
//           <style>
//             @page { size: A4; margin: 15mm; }

//             body {
//               font-family: Arial, sans-serif;
//               margin: 0;
//               color: #111;
//               position: relative;
//             }

//             .watermark {
//               position: fixed;
//               top: 50%;
//               left: 50%;
//               transform: translate(-50%, -50%);
//               opacity: 0.05;
//               z-index: 0;
//             }

//             .watermark img { width: 380px; }

//             .invoice {
//               position: relative;
//               z-index: 1;
//               padding: 10px;
//             }

//             .header {
//               text-align: center;
//               padding-bottom: 12px;
//               border-bottom: 3px solid #6366f1;
//             }

//             .logo {
//               height: 70px;
//               margin-bottom: 8px;
//             }

//             .title {
//               font-size: 22px;
//               font-weight: bold;
//             }

//             .sub {
//               font-size: 12px;
//               color: #6b7280;
//             }

//             .infoWrap {
//               margin-top: 18px;
//               display: grid;
//               grid-template-columns: 1fr 1fr;
//               gap: 14px;
//             }

//             .infoBox {
//               border: 1px solid #d1d5db;
//               border-radius: 12px;
//               overflow: hidden;
//               background: #fff;
//             }

//             .infoHead {
//               background: #f3f4f6;
//               padding: 10px 12px;
//               font-weight: 700;
//               font-size: 13px;
//               border-bottom: 1px solid #e5e7eb;
//             }

//             .infoBody {
//               padding: 0;
//             }

//             .infoRow {
//               display: grid;
//               grid-template-columns: 110px 1fr;
//               border-bottom: 1px solid #f1f5f9;
//             }

//             .infoRow:last-child {
//               border-bottom: none;
//             }

//             .infoLabel {
//               background: #fafafa;
//               padding: 10px 12px;
//               font-size: 12px;
//               font-weight: 700;
//               color: #4b5563;
//               border-right: 1px solid #f1f5f9;
//             }

//             .infoValue {
//               padding: 10px 12px;
//               font-size: 13px;
//               font-weight: 600;
//             }

//             table {
//               width: 100%;
//               border-collapse: collapse;
//               margin-top: 20px;
//             }

//             th {
//               background: #6366f1;
//               color: #fff;
//               padding: 10px;
//               font-size: 13px;
//             }

//             td {
//               border: 1px solid #e5e7eb;
//               padding: 8px;
//               text-align: center;
//               font-size: 13px;
//             }

//             tr:nth-child(even) {
//               background: #f9fafb;
//             }

//             .total {
//               margin-top: 18px;
//               display: flex;
//               justify-content: flex-end;
//             }

//             .totalBox {
//               min-width: 250px;
//               border: 1px solid #d1d5db;
//               border-radius: 12px;
//               overflow: hidden;
//             }

//             .totalRow {
//               display: grid;
//               grid-template-columns: 1fr 140px;
//             }

//             .totalLabel {
//               padding: 12px 14px;
//               background: #f9fafb;
//               font-weight: 700;
//             }

//             .totalValue {
//               padding: 12px 14px;
//               background: #ecfdf5;
//               font-weight: 800;
//               text-align: right;
//               color: #047857;
//             }

//             .footer {
//               margin-top: 40px;
//               display: flex;
//               justify-content: space-between;
//               align-items: flex-end;
//             }

//             .note {
//               font-size: 12px;
//               color: #374151;
//               max-width: 260px;
//               line-height: 1.5;
//             }

//             .sign-line {
//               margin-top: 60px;
//               border-top: 1px solid #111;
//               width: 200px;
//               text-align: center;
//               padding-top: 5px;
//               font-size: 12px;
//               font-weight: 600;
//             }
//           </style>
//         </head>
//         <body>
//           <div class="watermark">
//             <img src="/mtlogo.jpg" />
//           </div>

//           <div class="invoice">
//             <div class="header">
//               <img src="/mtlogo.jpg" class="logo" />
//               <div class="title">APPROVAL MEMO</div>
//               <div class="sub">Provisional Invoice</div>
//             </div>

//             <div class="infoWrap">
//               <div class="infoBox">
//                 <div class="infoHead">Customer Details</div>
//                 <div class="infoBody">
//                   <div class="infoRow">
//                     <div class="infoLabel">Name</div>
//                     <div class="infoValue">${payload.customerName || "-"}</div>
//                   </div>
//                   <div class="infoRow">
//                     <div class="infoLabel">Mobile</div>
//                     <div class="infoValue">${payload.mobile || "-"}</div>
//                   </div>
//                 </div>
//               </div>

//               <div class="infoBox">
//                 <div class="infoHead">Memo Details</div>
//                 <div class="infoBody">
//                   <div class="infoRow">
//                     <div class="infoLabel">Date</div>
//                     <div class="infoValue">${payload.invoiceDate || "-"}</div>
//                   </div>
//                   <div class="infoRow">
//                     <div class="infoLabel">Created By</div>
//                     <div class="infoValue">${payload.createdBy || "-"}</div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             <table>
//               <thead>
//                 <tr>
//                   <th>#</th>
//                   <th>Product</th>
//                   <th>Company</th>
//                   <th>Design</th>
//                   <th>Qty</th>
//                   <th>Rate</th>
//                   <th>Total</th>
//                 </tr>
//               </thead>
//               <tbody>${rows}</tbody>
//             </table>

//             <div class="total">
//               <div class="totalBox">
//                 <div class="totalRow">
//                   <div class="totalLabel">Grand Total</div>
//                   <div class="totalValue">₹${Number(payload.grandTotal || 0).toLocaleString("en-IN")}</div>
//                 </div>
//               </div>
//             </div>

//             <div class="footer">
//               <div class="note">
//                 <b>Note:</b><br />
//                 This is a provisional invoice / approval memo.
//               </div>
//               <div>
//                 <div class="sign-line">Authorized Signature</div>
//               </div>
//             </div>
//           </div>

//           <script>
//             window.onload = function(){ window.print(); }
//           </script>
//         </body>
//       </html>
//     `);

//     w.document.close();
//   };

//   const handleSave = async (printAfter = false) => {
//     if (!validate()) return;

//     const payload = buildPayload();

//     try {
//       setIsSaving(true);
//       let savedPayload = payload;

//       if (typeof onSave === "function") {
//         const resp = await onSave(payload);
//         savedPayload = resp?.data || resp || payload;
//       }

//       if (printAfter) printInvoice(savedPayload);
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   if (!open) return null;

//   const canAdd =
//     draft.productName &&
//     draft.companyName &&
//     draft.designNumber &&
//     Number(draft.qty || 0) > 0;

//   return (
//     <div className="invOverlay" onClick={(e) => e.target === e.currentTarget && onClose?.()}>
//       <div className="invModal" onClick={(e) => e.stopPropagation()}>
//         <div className="invHeader">
//           <div className="invHeaderLeft">
//             <div className="invLogoCircle">
//               <img src="/mtlogo.jpg" alt="logo" />
//             </div>
//             <div>
//               <div className="invTitle">Approval Memo</div>
//               <div className="invSub">Provisional Invoice • {user?.username || "Admin"}</div>
//             </div>
//           </div>

//           <button className="invClose" onClick={onClose} aria-label="Close">
//             <FaTimes />
//           </button>
//         </div>

//         <div className="invScrollArea">
//           <div className="invTop">
//             <div className="inv-field">
//               <label className="inv-label">Date</label>
//               <input
//                 className="inv-input"
//                 type="date"
//                 value={invoiceDate}
//                 onChange={(e) => setInvoiceDate(e.target.value)}
//               />
//             </div>

//             <div className="inv-field">
//               <label className="inv-label">Customer Name</label>
//               <input
//                 className="inv-input"
//                 value={customerName}
//                 onChange={(e) => setCustomerName(e.target.value)}
//                 placeholder="Enter customer name"
//               />
//               {errors.customerName && <div className="invErr">{errors.customerName}</div>}
//             </div>

//             <div className="inv-field">
//               <label className="inv-label">Mobile Number</label>
//               <input
//                 className="inv-input"
//                 type="tel"
//                 inputMode="numeric"
//                 value={mobile}
//                 onChange={(e) => {
//                   const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
//                   setMobile(digits);
//                 }}
//                 placeholder="10 digit mobile number"
//               />
//               {errors.mobile && <div className="invErr">{errors.mobile}</div>}
//             </div>
//           </div>

//           <div className="invBody">
//             <div className="invAddBox">
//               <div className="invSectionTitle">Add Item</div>

//               <div className="invAddGrid">
//                 <SearchSelect
//                   label="Product Name"
//                   value={draft.productName}
//                   options={productOptions}
//                   placeholder="Search product..."
//                   disabled={false}
//                   onChange={handleProductChange}
//                 />

//                 <SearchSelect
//                   label="Company Name"
//                   value={draft.companyName}
//                   options={companyOptions}
//                   placeholder="Search company..."
//                   disabled={!draft.productName}
//                   onChange={handleCompanyChange}
//                 />

//                 <SearchSelect
//                   label="Design No"
//                   value={draft.designNumber}
//                   options={designOptions}
//                   placeholder="Search design..."
//                   disabled={!draft.productName || !draft.companyName}
//                   onChange={handleDesignChange}
//                 />

//                 <div className="inv-field">
//                   <label className="inv-label">W Price</label>
//                   <input className="inv-input invReadOnly" value={draft.wRate} readOnly />
//                 </div>

//                 <div className="inv-field">
//                   <label className="inv-label">Qty</label>
//                   <input
//                     className="inv-input"
//                     type="number"
//                     min="1"
//                     value={draft.qty}
//                     disabled={!draft.designNumber}
//                     onChange={(e) => setDraft((p) => ({ ...p, qty: e.target.value }))}
//                   />
//                 </div>

//                 <div className="inv-field">
//                   <label className="inv-label">Amount</label>
//                   <input className="inv-input invReadOnly invGreen" value={draftLineTotal} readOnly />
//                 </div>

//                 <button className="invAddBtn" onClick={addItem} disabled={!canAdd}>
//                   <FaPlus />
//                   <span>Add Item</span>
//                 </button>
//               </div>
//             </div>

//             {errors.items && <div className="invErr invErrBox">{errors.items}</div>}

//             <div className="invDesktopTable">
//               <div className="invTableHead">
//                 <div>Product</div>
//                 <div>Company</div>
//                 <div>Design</div>
//                 <div>W Price</div>
//                 <div>Qty</div>
//                 <div>Total</div>
//                 <div>Remove</div>
//               </div>

//               {items.length === 0 ? (
//                 <div className="invEmpty">No items added</div>
//               ) : (
//                 <div className="invTable">
//                   {items.map((it, idx) => (
//                     <div key={it.id} className={`invRow ${idx % 2 === 0 ? "even" : "odd"}`}>
//                       <div className="invStrong">{it.productName}</div>
//                       <div>{it.companyName}</div>
//                       <div className="invStrong">{it.designNumber}</div>
//                       <div>{money(it.wRate)}</div>
//                       <div>{it.qty}</div>
//                       <div className="invGreenStrong">{money(it.total)}</div>
//                       <button className="invRemove" onClick={() => removeItem(it.id)}>
//                         <FaTrash />
//                       </button>
//                     </div>
//                   ))}
//                 </div>
//               )}
//             </div>

//             <div className="invMobileCards">
//               {items.length === 0 ? (
//                 <div className="invEmpty">No items added</div>
//               ) : (
//                 items.map((it, index) => (
//                   <div key={it.id} className="invCard">
//                     <div className="invCardTop">
//                       <div className="invCardTopLeft">
//                         <div className="invCardIndex">Item #{index + 1}</div>
//                         <div className="invCardTitle">{it.productName}</div>
//                       </div>
//                       <button className="invRemove" onClick={() => removeItem(it.id)}>
//                         <FaTrash />
//                       </button>
//                     </div>

//                     <div className="invCardRows">
//                       <div className="invCardRow">
//                         <span className="invMini">Company</span>
//                         <span className="invCardValue">{it.companyName}</span>
//                       </div>
//                       <div className="invCardRow">
//                         <span className="invMini">Design</span>
//                         <span className="invCardValue invStrong">{it.designNumber}</span>
//                       </div>
//                       <div className="invCardRow">
//                         <span className="invMini">W Price</span>
//                         <span className="invCardValue">{money(it.wRate)}</span>
//                       </div>
//                       <div className="invCardRow">
//                         <span className="invMini">Qty</span>
//                         <span className="invCardValue">{it.qty}</span>
//                       </div>
//                       <div className="invCardRow totalRow">
//                         <span className="invMini">Total</span>
//                         <span className="invCardValue invGreenStrong">{money(it.total)}</span>
//                       </div>
//                     </div>
//                   </div>
//                 ))
//               )}
//             </div>
//           </div>
//         </div>

//         <div className="invFooter">
//           <div className="invFootLeft">Items: {items.length}</div>

//           <div className="invFooterRight">
//             <div className="invGrandCompact">
//               <span>Grand Total</span>
//               <b>{money(grandTotal)}</b>
//             </div>

//             <button
//               className="invBtn invBtnPrimary"
//               onClick={() => handleSave(true)}
//               disabled={isSaving}
//             >
//               <FaPrint /> {isSaving ? "Saving..." : "Save & Print"}
//             </button>
//           </div>
//         </div>
//       </div>

//       <style>{`
//         .invOverlay{
//           position:fixed;
//           inset:0;
//           background:rgba(0,0,0,0.55);
//           display:flex;
//           align-items:center;
//           justify-content:center;
//           z-index:3000;
//           padding:12px;
//         }

//         .invModal{
//           width:100%;
//           max-width:1180px;
//           max-height:95vh;
//           background:#fff;
//           border-radius:18px;
//           display:flex;
//           flex-direction:column;
//           overflow:hidden;
//           box-shadow:0 20px 60px rgba(0,0,0,0.22);
//         }

//         .invHeader{
//           background:linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
//           color:#fff;
//           padding:14px 18px;
//           display:flex;
//           align-items:center;
//           justify-content:space-between;
//           gap:12px;
//           flex-shrink:0;
//         }

//         .invHeaderLeft{
//           display:flex;
//           align-items:center;
//           gap:12px;
//           min-width:0;
//         }

//         .invLogoCircle{
//           width:48px;
//           height:48px;
//           border-radius:50%;
//           background:rgba(255,255,255,0.18);
//           display:flex;
//           align-items:center;
//           justify-content:center;
//           overflow:hidden;
//           border:2px solid rgba(255,255,255,0.25);
//           flex-shrink:0;
//         }

//         .invLogoCircle img{
//           width:100%;
//           height:100%;
//           object-fit:cover;
//         }

//         .invTitle{
//           font-size:18px;
//           font-weight:900;
//           line-height:1.2;
//         }

//         .invSub{
//           font-size:12px;
//           margin-top:3px;
//           font-weight:700;
//           opacity:.95;
//         }

//         .invClose{
//           width:38px;
//           height:38px;
//           border:none;
//           border-radius:10px;
//           background:rgba(255,255,255,.18);
//           color:#fff;
//           cursor:pointer;
//           display:flex;
//           align-items:center;
//           justify-content:center;
//           flex-shrink:0;
//         }

//         .invScrollArea{
//           flex:1;
//           overflow:auto;
//           min-height:0;
//         }

//         .invTop{
//           padding:16px 18px;
//           border-bottom:1px solid #e5e7eb;
//           display:grid;
//           grid-template-columns:repeat(3, 1fr);
//           gap:14px;
//         }

//         .invBody{
//           padding:16px 18px 16px;
//         }

//         .invSectionTitle{
//           font-size:14px;
//           font-weight:900;
//           color:#111827;
//           margin-bottom:12px;
//         }

//         .inv-field{
//           position:relative;
//           min-width:0;
//         }

//         .inv-label{
//           display:block;
//           font-size:12px;
//           font-weight:900;
//           color:#374151;
//           margin-bottom:6px;
//         }

//         .inv-input{
//           width:100%;
//           height:46px;
//           padding:0 12px;
//           border-radius:12px;
//           border:1px solid #d1d5db;
//           outline:none;
//           font-size:14px;
//           box-sizing:border-box;
//           background:#fff;
//         }

//         .inv-input:focus{
//           border-color:#6366f1;
//           box-shadow:0 0 0 3px rgba(99,102,241,0.12);
//         }

//         .inv-input:disabled{
//           background:#f3f4f6;
//           cursor:not-allowed;
//         }

//         .invReadOnly{
//           background:#f3f4f6 !important;
//           font-weight:900;
//         }

//         .invGreen{
//           color:#047857;
//           font-weight:900;
//         }

//         .invErr{
//           margin-top:6px;
//           font-size:12px;
//           font-weight:800;
//           color:#ef4444;
//         }

//         .invErrBox{
//           margin-top:12px;
//           background:#fef2f2;
//           border:1px solid #fecaca;
//           padding:10px 12px;
//           border-radius:12px;
//         }

//         .invAddBox{
//           background:#f9fafb;
//           border:1px solid #e5e7eb;
//           border-radius:16px;
//           padding:14px;
//         }

//         .invAddGrid{
//           display:grid;
//           grid-template-columns: 2fr 2fr 1.7fr 1fr 0.8fr 1fr 1fr;
//           gap:12px;
//           align-items:end;
//         }

//         .invAddBtn{
//           height:46px;
//           border:none;
//           border-radius:12px;
//           background:linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
//           color:#fff;
//           font-weight:900;
//           cursor:pointer;
//           display:flex;
//           align-items:center;
//           justify-content:center;
//           gap:8px;
//           white-space:nowrap;
//         }

//         .invAddBtn:disabled{
//           background:#d1d5db;
//           cursor:not-allowed;
//         }

//         .inv-selectWrap{
//           position:relative;
//         }

//         .inv-dropdown{
//           position:absolute;
//           left:0;
//           right:0;
//           top:calc(100% + 6px);
//           background:#fff;
//           border:1px solid #e5e7eb;
//           border-radius:14px;
//           box-shadow:0 12px 30px rgba(0,0,0,0.12);
//           max-height:240px;
//           overflow:auto;
//           z-index:100;
//           padding:6px;
//         }

//         .inv-ddItem{
//           width:100%;
//           text-align:left;
//           border:none;
//           background:transparent;
//           padding:10px;
//           border-radius:10px;
//           cursor:pointer;
//           font-size:13px;
//           font-weight:700;
//           color:#111827;
//         }

//         .inv-ddItem:hover{
//           background:#f3f4f6;
//         }

//         .inv-ddItem.active{
//           background:#eef2ff;
//           color:#3730a3;
//         }

//         .inv-ddEmpty{
//           padding:10px;
//           color:#6b7280;
//           font-weight:700;
//           font-size:13px;
//         }

//         .isDisabled .inv-input{
//           background:#f3f4f6;
//           cursor:not-allowed;
//         }

//         .invDesktopTable{
//           margin-top:16px;
//         }

//         .invTableHead{
//           display:grid;
//           grid-template-columns:1.3fr 1.2fr 1fr 0.9fr 0.7fr 1fr 70px;
//           gap:10px;
//           padding:10px 12px;
//           color:#6b7280;
//           font-size:12px;
//           font-weight:900;
//         }

//         .invTable{
//           border:1px solid #e5e7eb;
//           border-radius:16px;
//           overflow:hidden;
//         }

//         .invRow{
//           display:grid;
//           grid-template-columns:1.3fr 1.2fr 1fr 0.9fr 0.7fr 1fr 70px;
//           gap:10px;
//           padding:12px;
//           align-items:center;
//           font-size:13px;
//         }

//         .invRow.even{ background:#fff; }
//         .invRow.odd{ background:#fafafa; }

//         .invStrong{
//           font-weight:900;
//           color:#111827;
//         }

//         .invGreenStrong{
//           font-weight:900;
//           color:#047857;
//         }

//         .invRemove{
//           border:1px solid #fecaca;
//           background:#fff;
//           color:#ef4444;
//           border-radius:12px;
//           padding:10px;
//           cursor:pointer;
//           display:flex;
//           align-items:center;
//           justify-content:center;
//           min-width:42px;
//           min-height:42px;
//         }

//         .invEmpty{
//           padding:18px;
//           border:1px dashed #d1d5db;
//           border-radius:16px;
//           text-align:center;
//           color:#6b7280;
//           font-weight:800;
//           margin-top:10px;
//           background:#fff;
//         }

//         .invMobileCards{
//           display:none;
//           margin-top:16px;
//         }

//         .invCard{
//           border:1px solid #e5e7eb;
//           border-radius:16px;
//           background:#fff;
//           padding:14px;
//           margin-bottom:12px;
//         }

//         .invCardTop{
//           display:flex;
//           justify-content:space-between;
//           align-items:flex-start;
//           gap:10px;
//           margin-bottom:10px;
//         }

//         .invCardTopLeft{
//           min-width:0;
//         }

//         .invCardIndex{
//           font-size:11px;
//           font-weight:900;
//           color:#6366f1;
//           margin-bottom:4px;
//         }

//         .invCardTitle{
//           font-size:15px;
//           font-weight:900;
//           color:#111827;
//           line-height:1.3;
//           word-break:break-word;
//         }

//         .invCardRows{
//           display:flex;
//           flex-direction:column;
//           gap:8px;
//           margin-top:8px;
//         }

//         .invCardRow{
//           display:flex;
//           justify-content:space-between;
//           gap:12px;
//           padding:10px 12px;
//           background:#f9fafb;
//           border:1px solid #eef2f7;
//           border-radius:12px;
//         }

//         .invCardValue{
//           font-size:13px;
//           font-weight:800;
//           color:#111827;
//           text-align:right;
//           word-break:break-word;
//         }

//         .totalRow{
//           background:#ecfdf5;
//           border-color:#a7f3d0;
//         }

//         .invMini{
//           font-size:11px;
//           color:#6b7280;
//           font-weight:900;
//           flex-shrink:0;
//         }

//         .invFooter{
//           border-top:1px solid #e5e7eb;
//           background:#f9fafb;
//           padding:12px 18px;
//           display:flex;
//           justify-content:space-between;
//           align-items:center;
//           gap:12px;
//           flex-wrap:wrap;
//           flex-shrink:0;
//         }

//         .invFootLeft{
//           font-size:12px;
//           color:#6b7280;
//           font-weight:900;
//         }

//         .invFooterRight{
//           display:flex;
//           align-items:center;
//           gap:10px;
//           flex-wrap:wrap;
//           margin-left:auto;
//         }

//         .invGrandCompact{
//           display:flex;
//           align-items:center;
//           gap:10px;
//           background:#fff;
//           border:1px solid #e5e7eb;
//           border-radius:12px;
//           padding:10px 12px;
//           font-weight:900;
//           min-height:44px;
//         }

//         .invBtn{
//           border:none;
//           border-radius:12px;
//           padding:11px 16px;
//           font-size:13px;
//           font-weight:900;
//           cursor:pointer;
//           display:flex;
//           align-items:center;
//           justify-content:center;
//           gap:8px;
//           min-height:44px;
//         }

//         .invBtn:disabled{
//           opacity:.6;
//           cursor:not-allowed;
//         }

//         .invBtnPrimary{
//           background:linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
//           color:#fff;
//         }

//         @media (max-width: 1100px){
//           .invAddGrid{
//             grid-template-columns:repeat(3, 1fr);
//           }
//         }

//         @media (max-width: 900px){
//           .invTop{
//             grid-template-columns:1fr 1fr;
//           }

//           .invAddGrid{
//             grid-template-columns:repeat(2, 1fr);
//           }
//         }

//         @media (max-width: 768px){
//           .invOverlay{
//             padding:0;
//             align-items:flex-end;
//           }

//           .invModal{
//             max-width:100%;
//             width:100%;
//             height:100vh;
//             max-height:100vh;
//             border-radius:18px 18px 0 0;
//           }

//           .invTop{
//             grid-template-columns:1fr;
//             gap:12px;
//             padding:14px;
//           }

//           .invBody{
//             padding:14px;
//           }

//           .invAddBox{
//             padding:12px;
//           }

//           .invAddGrid{
//             grid-template-columns:1fr;
//             gap:12px;
//           }

//           .invDesktopTable{
//             display:none;
//           }

//           .invMobileCards{
//             display:block;
//           }

//           .invFooter{
//             flex-direction:column;
//             align-items:stretch;
//             padding:12px 14px;
//           }

//           .invFootLeft{
//             padding-top:0;
//           }

//           .invFooterRight{
//             width:100%;
//             margin-left:0;
//             flex-direction:column;
//             align-items:stretch;
//           }

//           .invGrandCompact{
//             width:100%;
//             justify-content:space-between;
//             box-sizing:border-box;
//           }

//           .invBtn{
//             width:100%;
//           }
//         }

//         @media (max-width: 480px){
//           .invHeader{
//             padding:12px 14px;
//           }

//           .invLogoCircle{
//             width:42px;
//             height:42px;
//           }

//           .invTitle{
//             font-size:16px;
//           }

//           .invSub{
//             font-size:11px;
//           }

//           .invCard{
//             padding:12px;
//           }

//           .invCardValue{
//             max-width:60%;
//           }
//         }
//       `}</style>
//     </div>
//   );
// }

// export default ProvisionalInvoiceModal;








// import React, { useEffect, useMemo, useRef, useState } from "react";
// import { FaPlus, FaPrint, FaSave, FaTimes, FaTrash } from "react-icons/fa";

// /**
//  * ProvisionalInvoiceModal (Approval Memo)
//  * ✅ Product / Company / Design = Search + Select ONLY (no new values)
//  * ✅ Design select -> W Rate auto fill
//  * ✅ Qty -> Total auto
//  * ✅ Add multiple items + remove
//  * ✅ Grand Total auto
//  * ✅ Mobile validation (digits only, exactly 10)
//  * ✅ Save, Print, Save & Print
//  * ✅ Responsive UI (desktop table + mobile cards)
//  *
//  * Optional:
//  *   onSave?: async (payload) => any
//  */
// function ProvisionalInvoiceModal({ open, onClose, products = [], user, onSave }) {
//   const todayISO = new Date().toISOString().split("T")[0];

//   const [invoiceDate, setInvoiceDate] = useState(todayISO);
//   const [customerName, setCustomerName] = useState("");
//   const [mobile, setMobile] = useState("");
//   const [errors, setErrors] = useState({});
//   const [isSaving, setIsSaving] = useState(false);

//   const [items, setItems] = useState([]);

//   const [draft, setDraft] = useState({
//     productName: "",
//     companyName: "",
//     designNumber: "",
//     wRate: 0,
//     qty: 1,
//   });

//   // reset on open
//   useEffect(() => {
//     if (!open) return;

//     setInvoiceDate(todayISO);
//     setCustomerName("");
//     setMobile("");
//     setErrors({});
//     setIsSaving(false);
//     setItems([]);
//     setDraft({
//       productName: "",
//       companyName: "",
//       designNumber: "",
//       wRate: 0,
//       qty: 1,
//     });

//     const onKeyDown = (e) => e.key === "Escape" && onClose?.();
//     window.addEventListener("keydown", onKeyDown);
//     return () => window.removeEventListener("keydown", onKeyDown);
//     // eslint-disable-next-line
//   }, [open]);

//   const money = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

//   // ===================== Options =====================
//   const productOptions = useMemo(
//     () => [...new Set(products.map((p) => p.productName).filter(Boolean))].sort(),
//     [products]
//   );

//   const companyOptions = useMemo(() => {
//     if (!draft.productName) return [];
//     return [
//       ...new Set(
//         products
//           .filter((p) => p.productName === draft.productName)
//           .map((p) => p.companyName)
//           .filter(Boolean)
//       ),
//     ].sort();
//   }, [products, draft.productName]);

//   const designOptions = useMemo(() => {
//     if (!draft.productName || !draft.companyName) return [];
//     return [
//       ...new Set(
//         products
//           .filter((p) => p.productName === draft.productName && p.companyName === draft.companyName)
//           .map((p) => p.designNumber)
//           .filter(Boolean)
//       ),
//     ].sort();
//   }, [products, draft.productName, draft.companyName]);

//   // ===================== Calculations =====================
//   const draftLineTotal = useMemo(() => {
//     const rate = Number(draft.wRate || 0);
//     const qty = Number(draft.qty || 0);
//     return rate * qty;
//   }, [draft.wRate, draft.qty]);

//   const grandTotal = useMemo(() => items.reduce((sum, it) => sum + Number(it.total || 0), 0), [items]);

//   // ===================== SearchSelect (select-only) =====================
//   function SearchSelect({ label, value, options, placeholder, disabled, onChange }) {
//     const wrapRef = useRef(null);
//     const [openList, setOpenList] = useState(false);
//     const [query, setQuery] = useState(value || "");

//     useEffect(() => {
//       setQuery(value || "");
//     }, [value]);

//     useEffect(() => {
//       const onDocDown = (e) => {
//         if (!wrapRef.current) return;
//         if (!wrapRef.current.contains(e.target)) {
//           setOpenList(false);
//           // enforce select-only: if query not exact match => revert to selected value
//           if (query && !options.includes(query)) setQuery(value || "");
//         }
//       };
//       document.addEventListener("mousedown", onDocDown);
//       return () => document.removeEventListener("mousedown", onDocDown);
//     }, [query, options, value]);

//     const filtered = useMemo(() => {
//       const q = query.trim().toLowerCase();
//       if (!q) return options;
//       return options.filter((o) => o.toLowerCase().includes(q));
//     }, [options, query]);

//     const selectValue = (v) => {
//       onChange(v);
//       setQuery(v);
//       setOpenList(false);
//     };

//     return (
//       <div className="inv-field" ref={wrapRef}>
//         <label className="inv-label">{label}</label>

//         <div className={`inv-selectWrap ${disabled ? "isDisabled" : ""}`}>
//           <input
//             value={query}
//             disabled={disabled}
//             placeholder={placeholder}
//             className="inv-input"
//             onFocus={() => !disabled && setOpenList(true)}
//             onChange={(e) => {
//               const val = e.target.value;
//               setQuery(val);
//               setOpenList(true);

//               // select-only behavior: typing doesn't set parent value
//               // if user typed exact match, auto-select
//               if (options.includes(val)) {
//                 onChange(val);
//               } else {
//                 // if currently selected doesn't match typed, clear selected in parent
//                 if (value) onChange("");
//               }
//             }}
//             onBlur={() => {
//               // blur: if not exact match, revert
//               setTimeout(() => {
//                 if (query && !options.includes(query)) setQuery(value || "");
//                 setOpenList(false);
//               }, 120);
//             }}
//           />

//           {!disabled && openList && (
//             <div className="inv-dropdown" role="listbox">
//               {filtered.length === 0 ? (
//                 <div className="inv-ddEmpty">No results</div>
//               ) : (
//                 filtered.slice(0, 80).map((opt) => (
//                   <button
//                     type="button"
//                     key={opt}
//                     className={`inv-ddItem ${opt === value ? "active" : ""}`}
//                     onMouseDown={(e) => e.preventDefault()}
//                     onClick={() => selectValue(opt)}
//                   >
//                     {opt}
//                   </button>
//                 ))
//               )}
//             </div>
//           )}
//         </div>

//         <div className="inv-helper">
//           {disabled ? "Select previous field first" : `${options.length} options`}
//         </div>
//       </div>
//     );
//   }

//   // ===================== Change handlers =====================
//   const handleProductChange = (val) => {
//     setDraft((p) => ({
//       ...p,
//       productName: val,
//       companyName: "",
//       designNumber: "",
//       wRate: 0,
//     }));
//   };

//   const handleCompanyChange = (val) => {
//     setDraft((p) => ({
//       ...p,
//       companyName: val,
//       designNumber: "",
//       wRate: 0,
//     }));
//   };

//   const handleDesignChange = (val) => {
//     const found = products.find(
//       (p) =>
//         p.productName === draft.productName &&
//         p.companyName === draft.companyName &&
//         p.designNumber === val
//     );
//     setDraft((p) => ({
//       ...p,
//       designNumber: val,
//       wRate: Number(found?.wRate ?? 0),
//     }));
//   };

//   const addItem = () => {
//     const qty = Number(draft.qty || 0);
//     if (!draft.productName || !draft.companyName || !draft.designNumber) return;
//     if (!qty || qty <= 0) return;

//     const id =
//       window.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;

//     const item = {
//       id,
//       productName: draft.productName,
//       companyName: draft.companyName,
//       designNumber: draft.designNumber,
//       wRate: Number(draft.wRate || 0),
//       qty,
//       total: Number(draft.wRate || 0) * qty,
//     };

//     setItems((prev) => [...prev, item]);

//     // keep product/company for faster next add; reset design
//     setDraft((p) => ({
//       ...p,
//       designNumber: "",
//       wRate: 0,
//       qty: 1,
//     }));
//   };

//   const removeItem = (id) => setItems((prev) => prev.filter((x) => x.id !== id));

//   // ===================== Validation =====================
//   const validate = () => {
//     const next = {};
//     const digits = (mobile || "").replace(/\D/g, "");

//     if (!customerName.trim()) next.customerName = "Customer name is required";
//     if (!digits) next.mobile = "Mobile number is required";
//     else if (digits.length !== 10) next.mobile = "Mobile number must be 10 digits";
//     if (items.length === 0) next.items = "Add at least 1 item";

//     setErrors(next);
//     return Object.keys(next).length === 0;
//   };

//   const buildPayload = () => ({
//     type: "APPROVAL_MEMO",
//     invoiceDate,
//     customerName: customerName.trim(),
//     mobile,
//     items,
//     grandTotal,
//     createdOn: new Date().toISOString(),
//     createdBy: user?.username || "Admin",
//   });

//   // ===================== Print (Full invoice look) =====================
// const printInvoice = (payload) => {
//   const w = window.open("", "_blank", "width=900,height=700");
//   if (!w) return;

//   const rows = payload.items
//     .map(
//       (it, i) => `
//       <tr>
//         <td>${i + 1}</td>
//         <td>${it.productName}</td>
//         <td>${it.companyName}</td>
//         <td><b>${it.designNumber}</b></td>
//         <td>${it.qty}</td>
//         <td>₹${it.wRate}</td>
//         <td><b>₹${it.total}</b></td>
//       </tr>
//     `
//     )
//     .join("");

//   w.document.write(`
//     <html>
//       <head>
//         <title>Approval Memo</title>

//         <style>
//           @page { size: A4; margin: 15mm; }

//           body {
//             font-family: Arial, sans-serif;
//             margin: 0;
//             color: #111;
//             position: relative;
//           }

//           /* 🔥 WATERMARK BACKGROUND LOGO */
//           .watermark {
//             position: fixed;
//             top: 50%;
//             left: 50%;
//             transform: translate(-50%, -50%);
//             opacity: 0.06;
//             z-index: 0;
//           }

//           .watermark img {
//             width: 400px;
//           }

//           .invoice {
//             position: relative;
//             z-index: 1;
//           }

//           /* 🔥 HEADER */
//           .header {
//             text-align: center;
//             padding-bottom: 12px;
//             border-bottom: 3px solid #6366f1;
//           }

//           .logo {
//             height: 70px;
//             margin-bottom: 8px;
//           }

//           .title {
//             font-size: 22px;
//             font-weight: bold;
//           }

//           .sub {
//             font-size: 12px;
//             color: #6b7280;
//           }

//           /* 🔥 BOXES */
//           .meta {
//             display: flex;
//             justify-content: space-between;
//             margin-top: 15px;
//             gap: 10px;
//           }

//           .box {
//             width: 48%;
//             border: 1px solid #d1d5db;
//             border-radius: 10px;
//             padding: 10px;
//             background: #f9fafb;
//           }

//           /* 🔥 TABLE */
//           table {
//             width: 100%;
//             border-collapse: collapse;
//             margin-top: 18px;
//           }

//           th {
//             background: #6366f1;
//             color: #fff;
//             padding: 10px;
//           }

//           td {
//             border: 1px solid #e5e7eb;
//             padding: 8px;
//             text-align: center;
//           }

//           tr:nth-child(even) {
//             background: #f9fafb;
//           }

//           /* 🔥 TOTAL */
//           .total {
//             margin-top: 18px;
//             text-align: right;
//             font-size: 18px;
//             font-weight: bold;
//           }

//           /* 🔥 FOOTER */
//           .footer {
//             margin-top: 40px;
//             display: flex;
//             justify-content: space-between;
//           }

//           .sign-line {
//             margin-top: 60px;
//             border-top: 1px solid #111;
//             width: 200px;
//             text-align: center;
//             padding-top: 5px;
//           }

//         </style>
//       </head>

//       <body>

//         <!-- 🔥 WATERMARK -->
//         <div class="watermark">
//           <img src="/mtlogo.jpg" />
//         </div>

//         <div class="invoice">

//           <!-- 🔥 HEADER -->
//           <div class="header">
//             <img src="/mtlogo.jpg" class="logo" />
//             <div class="title">APPROVAL MEMO</div>
//             <div class="sub">Provisional Invoice</div>
//           </div>

//           <!-- 🔥 DETAILS -->
//           <div class="meta">
//             <div class="box">
//               <b>Customer</b><br/><br/>
//               ${payload.customerName}<br/>
//               ${payload.mobile}
//             </div>

//             <div class="box">
//               <b>Details</b><br/><br/>
//               Date: ${payload.invoiceDate}<br/>
//               Created By: ${payload.createdBy}
//             </div>
//           </div>

//           <!-- 🔥 TABLE -->
//           <table>
//             <thead>
//               <tr>
//                 <th>#</th>
//                 <th>Product</th>
//                 <th>Company</th>
//                 <th>Design</th>
//                 <th>Qty</th>
//                 <th>Rate</th>
//                 <th>Total</th>
//               </tr>
//             </thead>

//             <tbody>
//               ${rows}
//             </tbody>
//           </table>

//           <!-- 🔥 TOTAL -->
//           <div class="total">
//             Grand Total: ₹${payload.grandTotal}
//           </div>

//           <!-- 🔥 FOOTER -->
//           <div class="footer">
//             <div>
//               <b>Note:</b><br/>
//               This is a provisional invoice.
//             </div>

//             <div>
//               <div class="sign-line">Authorized Signature</div>
//             </div>
//           </div>

//         </div>

//         <script>
//           window.onload = function(){
//             window.print();
//           }
//         </script>

//       </body>
//     </html>
//   `);

//   w.document.close();
// };

//   // ===================== Actions =====================
//   const handleSave = async (printAfter = false) => {
//     if (!validate()) return;

//     const payload = buildPayload();

//     try {
//       setIsSaving(true);

//       let savedPayload = payload;
//       if (typeof onSave === "function") {
//         const resp = await onSave(payload);
//         savedPayload = resp?.data || resp || payload;
//       }

//       if (printAfter) printInvoice(savedPayload);
//     } finally {
//       setIsSaving(false);
//     }
//   };

//   if (!open) return null;

//   const canAdd =
//     draft.productName &&
//     draft.companyName &&
//     draft.designNumber &&
//     Number(draft.qty || 0) > 0;

//   return (
//     <div
//       className="invOverlay"
//       onClick={(e) => e.target === e.currentTarget && onClose?.()}
//     >
//       <div className="invModal" onClick={(e) => e.stopPropagation()}>
//         {/* Header */}
//         <div className="invHeader">
//           <div>
//             <div className="invTitle">Approval Memo</div>
//             <div className="invSub">Provisional Invoice • {user?.username || "Admin"}</div>
//           </div>
//           <button className="invClose" onClick={onClose} aria-label="Close">
//             <FaTimes />
//           </button>
//         </div>

//         {/* Top */}
//         <div className="invTop">
//           <div className="inv-field">
//             <label className="inv-label">Date</label>
//             <input
//               className="inv-input"
//               type="date"
//               value={invoiceDate}
//               onChange={(e) => setInvoiceDate(e.target.value)}
//             />
//           </div>

//           <div className="inv-field">
//             <label className="inv-label">Customer Name</label>
//             <input
//               className="inv-input"
//               value={customerName}
//               onChange={(e) => setCustomerName(e.target.value)}
//               placeholder="Enter customer name"
//             />
//             {errors.customerName ? <div className="invErr">{errors.customerName}</div> : null}
//           </div>

//           <div className="inv-field">
//             <label className="inv-label">Mobile Number</label>
//             <input
//               className="inv-input"
//               type="tel"
//               inputMode="numeric"
//               pattern="[0-9]*"
//               value={mobile}
//               onChange={(e) => {
//                 const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
//                 setMobile(digits);
//               }}
//               placeholder="10 digit"
//               onBlur={() => {
//                 const next = { ...errors };
//                 if (!mobile) next.mobile = "Mobile number is required";
//                 else if (mobile.length !== 10) next.mobile = "Mobile number must be 10 digits";
//                 else delete next.mobile;
//                 setErrors(next);
//               }}
//             />
//             {errors.mobile ? <div className="invErr">{errors.mobile}</div> : null}
//           </div>
//         </div>

//         {/* Body */}
//         <div className="invBody">
//           {/* Add Item */}
//           <div className="invAddBox">
//             <div className="invAddGrid">
//               <SearchSelect
//                 label="Product Name"
//                 value={draft.productName}
//                 options={productOptions}
//                 placeholder="Search product..."
//                 disabled={false}
//                 onChange={handleProductChange}
//               />

//               <SearchSelect
//                 label="Company Name"
//                 value={draft.companyName}
//                 options={companyOptions}
//                 placeholder="Search company..."
//                 disabled={!draft.productName}
//                 onChange={handleCompanyChange}
//               />

//               <SearchSelect
//                 label="Design No"
//                 value={draft.designNumber}
//                 options={designOptions}
//                 placeholder="Search design..."
//                 disabled={!draft.productName || !draft.companyName}
//                 onChange={handleDesignChange}
//               />

//               <div className="inv-field">
//                 <label className="inv-label">W Price</label>
//                 <input className="inv-input invReadOnly" value={draft.wRate} readOnly />
//               </div>

//               <div className="inv-field">
//                 <label className="inv-label">Qty</label>
//                 <input
//                   className="inv-input"
//                   type="number"
//                   min="1"
//                   value={draft.qty}
//                   disabled={!draft.designNumber}
//                   onChange={(e) => setDraft((p) => ({ ...p, qty: e.target.value }))}
//                 />
//               </div>

//               <div className="inv-field">
//                 <label className="inv-label">Total</label>
//                 <input className="inv-input invReadOnly invGreen" value={draftLineTotal} readOnly />
//               </div>

//               <button
//                 className="invAddBtn"
//                 onClick={addItem}
//                 disabled={!canAdd}
//                 title="Add Item"
//               >
//                 <FaPlus />
//                 <span>Add</span>
//               </button>
//             </div>
//           </div>

//           {errors.items ? <div className="invErr invErrBox">{errors.items}</div> : null}

//           {/* Desktop table */}
//           <div className="invTableWrap">
//             <div className="invTableHead">
//               <div>Product</div>
//               <div>Company</div>
//               <div>Design</div>
//               <div>W Price</div>
//               <div>Qty</div>
//               <div>Total</div>
//               <div>Remove</div>
//             </div>

//             {items.length === 0 ? (
//               <div className="invEmpty">No items added</div>
//             ) : (
//               <div className="invTable">
//                 {items.map((it, idx) => (
//                   <div key={it.id} className={`invRow ${idx % 2 === 0 ? "even" : "odd"}`}>
//                     <div className="invStrong">{it.productName}</div>
//                     <div>{it.companyName}</div>
//                     <div className="invStrong">{it.designNumber}</div>
//                     <div>{money(it.wRate)}</div>
//                     <div>{it.qty}</div>
//                     <div className="invGreenStrong">{money(it.total)}</div>
//                     <button className="invRemove" onClick={() => removeItem(it.id)} title="Remove">
//                       <FaTrash />
//                     </button>
//                   </div>
//                 ))}
//               </div>
//             )}
//           </div>

//           {/* Mobile cards */}
//           <div className="invCards">
//             {items.length === 0 ? (
//               <div className="invEmpty">No items added</div>
//             ) : (
//               items.map((it) => (
//                 <div key={it.id} className="invCard">
//                   <div className="invCardTop">
//                     <div>
//                       <div className="invCardTitle">{it.productName}</div>
//                       <div className="invCardSub">
//                         {it.companyName} • <span className="invStrong">{it.designNumber}</span>
//                       </div>
//                     </div>
//                     <button className="invRemove" onClick={() => removeItem(it.id)} title="Remove">
//                       <FaTrash />
//                     </button>
//                   </div>

//                   <div className="invCardGrid">
//                     <div>
//                       <div className="invMini">W Price</div>
//                       <div className="invStrong">{money(it.wRate)}</div>
//                     </div>
//                     <div>
//                       <div className="invMini">Qty</div>
//                       <div className="invStrong">{it.qty}</div>
//                     </div>
//                     <div>
//                       <div className="invMini">Total</div>
//                       <div className="invGreenStrong">{money(it.total)}</div>
//                     </div>
//                   </div>
//                 </div>
//               ))
//             )}
//           </div>
//         </div>

//         {/* Footer */}
//         <div className="invFooter">
//           <div className="invFootLeft">Items: {items.length}</div>

//           <div className="invFootRight">
//             <div className="invGrand">
//               <span>Grand Total</span>
//               <b>{money(grandTotal)}</b>
//             </div>

//             <div className="invActions">
//               <button
//                 className="invBtn invBtnOutline"
//                 onClick={() => printInvoice(buildPayload())}
//                 disabled={items.length === 0}
//               >
//                 <FaPrint /> Print
//               </button>

//               <button
//                 className="invBtn invBtnDark"
//                 onClick={() => handleSave(false)}
//                 disabled={isSaving}
//               >
//                 <FaSave /> {isSaving ? "Saving..." : "Save"}
//               </button>

//               <button
//                 className="invBtn invBtnPrimary"
//                 onClick={() => handleSave(true)}
//                 disabled={isSaving}
//               >
//                 <FaPrint /> {isSaving ? "Saving..." : "Save & Print"}
//               </button>
//             </div>
//           </div>
//         </div>
//       </div>

//       <style>{`
//         .invOverlay{
//           position:fixed; inset:0;
//           background:rgba(0,0,0,0.55);
//           display:flex; align-items:center; justify-content:center;
//           z-index:3000;
//           padding:12px;
//         }
//         .invModal{
//           width:100%;
//           max-width:1100px;
//           max-height:92vh;
//           display:flex; flex-direction:column;
//           background:#fff;
//           border-radius:16px;
//           overflow:hidden;
//           box-shadow:0 20px 60px rgba(0,0,0,0.22);
//         }

//         .invHeader{
//           background:linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
//           color:#fff;
//           padding:14px 16px;
//           display:flex; align-items:center; justify-content:space-between;
//           gap:10px;
//         }
//         .invTitle{ font-size:18px; font-weight:900; letter-spacing:.2px; }
//         .invSub{ font-size:12px; opacity:.92; margin-top:2px; font-weight:700; }
//         .invClose{
//           width:36px; height:36px;
//           border:none; border-radius:10px;
//           background:rgba(255,255,255,.18);
//           color:#fff; cursor:pointer;
//           display:flex; align-items:center; justify-content:center;
//         }

//         .invTop{
//           padding:14px 16px;
//           border-bottom:1px solid #e5e7eb;
//           display:grid;
//           grid-template-columns:180px 1fr 260px;
//           gap:12px;
//           align-items:end;
//         }

//         .invBody{
//           padding:14px 16px;
//           overflow:auto;
//         }

//         .inv-field{ position:relative; }
//         .inv-label{
//           display:block;
//           font-size:12px;
//           font-weight:900;
//           color:#374151;
//           margin-bottom:6px;
//         }
//         .inv-input{
//           width:100%;
//           padding:10px 12px;
//           border-radius:12px;
//           border:1px solid #d1d5db;
//           outline:none;
//           font-size:13px;
//           box-sizing:border-box;
//           background:#fff;
//         }
//         .inv-input:disabled{
//           background:#f3f4f6;
//           cursor:not-allowed;
//         }
//         .invReadOnly{
//           background:#f3f4f6 !important;
//           font-weight:900;
//         }
//         .invGreen{ color:#047857; font-weight:1000; }
//         .invErr{
//           margin-top:6px;
//           font-size:12px;
//           font-weight:900;
//           color:#ef4444;
//         }
//         .invErrBox{
//           margin-top:12px;
//           background:#fef2f2;
//           border:1px solid #fecaca;
//           padding:10px 12px;
//           border-radius:12px;
//         }

//         /* Add box */
//         .invAddBox{
//           background:#f9fafb;
//           border:1px solid #e5e7eb;
//           border-radius:16px;
//           padding:12px;
//         }
//         .invAddGrid{
//           display:grid;
//           grid-template-columns: 1.2fr 1.2fr 1fr 140px 110px 160px 120px;
//           gap:10px;
//           align-items:end;
//         }
//         .invAddBtn{
//           height:42px;
//           border:none;
//           border-radius:12px;
//           background:linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
//           color:#fff;
//           font-weight:1000;
//           cursor:pointer;
//           display:flex;
//           align-items:center;
//           justify-content:center;
//           gap:8px;
//         }
//         .invAddBtn:disabled{
//           background:#d1d5db;
//           cursor:not-allowed;
//         }

//         /* Search dropdown */
//         .inv-selectWrap{ position:relative; }
//         .inv-dropdown{
//           position:absolute;
//           left:0; right:0;
//           top:calc(100% + 6px);
//           background:#fff;
//           border:1px solid #e5e7eb;
//           border-radius:14px;
//           box-shadow:0 12px 30px rgba(0,0,0,0.12);
//           max-height:240px;
//           overflow:auto;
//           z-index:50;
//           padding:6px;
//         }
//         .inv-ddItem{
//           width:100%;
//           text-align:left;
//           border:none;
//           background:transparent;
//           padding:10px 10px;
//           border-radius:10px;
//           cursor:pointer;
//           font-size:13px;
//           font-weight:700;
//           color:#111827;
//         }
//         .inv-ddItem:hover{ background:#f3f4f6; }
//         .inv-ddItem.active{ background:#eef2ff; color:#3730a3; }
//         .inv-ddEmpty{
//           padding:10px;
//           color:#6b7280;
//           font-weight:800;
//           font-size:13px;
//         }
//         .inv-helper{
//           font-size:11px;
//           color:#6b7280;
//           font-weight:800;
//           margin-top:6px;
//         }
//         .isDisabled .inv-input{ background:#f3f4f6; cursor:not-allowed; }

//         /* Table */
//         .invTableWrap{ margin-top:14px; }
//         .invTableHead{
//           display:grid;
//           grid-template-columns:1.2fr 1.2fr 1fr 140px 110px 160px 70px;
//           gap:10px;
//           padding:10px 12px;
//           color:#6b7280;
//           font-size:12px;
//           font-weight:1000;
//         }
//         .invTable{
//           border:1px solid #e5e7eb;
//           border-radius:16px;
//           overflow:hidden;
//         }
//         .invRow{
//           display:grid;
//           grid-template-columns:1.2fr 1.2fr 1fr 140px 110px 160px 70px;
//           gap:10px;
//           padding:12px 12px;
//           align-items:center;
//           font-size:13px;
//         }
//         .invRow.even{ background:#fff; }
//         .invRow.odd{ background:#fafafa; }
//         .invStrong{ font-weight:1000; color:#111827; }
//         .invGreenStrong{ font-weight:1000; color:#047857; }
//         .invRemove{
//           border:1px solid #fecaca;
//           background:#fff;
//           color:#ef4444;
//           border-radius:12px;
//           padding:10px;
//           cursor:pointer;
//           display:flex;
//           align-items:center;
//           justify-content:center;
//         }
//         .invEmpty{
//           padding:18px;
//           border:1px dashed #d1d5db;
//           border-radius:16px;
//           text-align:center;
//           color:#6b7280;
//           font-weight:900;
//           margin-top:10px;
//         }

//         /* Mobile cards (hidden on desktop) */
//         .invCards{ display:none; margin-top:14px; }
//         .invCard{
//           border:1px solid #e5e7eb;
//           border-radius:16px;
//           background:#fff;
//           padding:12px;
//           margin-bottom:12px;
//         }
//         .invCardTop{
//           display:flex;
//           justify-content:space-between;
//           align-items:flex-start;
//           gap:10px;
//         }
//         .invCardTitle{ font-weight:1000; color:#111827; font-size:15px; }
//         .invCardSub{ font-size:12px; color:#6b7280; font-weight:800; margin-top:2px; }
//         .invCardGrid{
//           margin-top:12px;
//           display:grid;
//           grid-template-columns:1fr 1fr 1fr;
//           gap:10px;
//           background:#f9fafb;
//           border:1px solid #e5e7eb;
//           border-radius:14px;
//           padding:10px;
//         }
//         .invMini{ font-size:11px; color:#6b7280; font-weight:1000; margin-bottom:4px; }

//         /* Footer */
//         .invFooter{
//           border-top:1px solid #e5e7eb;
//           background:#f9fafb;
//           padding:14px 16px;
//           display:flex;
//           justify-content:space-between;
//           align-items:flex-start;
//           gap:12px;
//         }
//         .invFootLeft{
//           font-size:12px;
//           color:#6b7280;
//           font-weight:1000;
//           padding-top:6px;
//         }
//         .invFootRight{
//           display:flex;
//           flex-direction:column;
//           align-items:flex-end;
//           gap:10px;
//         }
//         .invGrand{
//           display:flex;
//           gap:10px;
//           align-items:center;
//           background:#fff;
//           border:1px solid #e5e7eb;
//           border-radius:14px;
//           padding:10px 12px;
//           font-weight:1000;
//         }
//         .invActions{
//           display:flex;
//           gap:10px;
//           flex-wrap:wrap;
//           justify-content:flex-end;
//         }
//         .invBtn{
//           border:none;
//           border-radius:12px;
//           padding:10px 12px;
//           font-size:13px;
//           font-weight:1000;
//           cursor:pointer;
//           display:flex;
//           align-items:center;
//           gap:8px;
//         }
//         .invBtn:disabled{ opacity:.6; cursor:not-allowed; }
//         .invBtnPrimary{
//           background:linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
//           color:#fff;
//         }
//         .invBtnDark{ background:#111827; color:#fff; }
//         .invBtnOutline{ background:#fff; color:#111827; border:1px solid #e5e7eb; }

//         /* Responsive */
//         @media (max-width: 980px){
//           .invAddGrid{
//             grid-template-columns: 1fr 1fr 1fr 120px 100px 140px 120px;
//           }
//           .invTableHead, .invRow{
//             grid-template-columns:1.2fr 1.2fr 1fr 120px 100px 140px 70px;
//           }
//           .invTop{ grid-template-columns:180px 1fr 240px; }
//         }

//         @media (max-width: 768px){
//           .invTop{ grid-template-columns:1fr; }
//           .invAddGrid{ grid-template-columns:1fr; }
//           .invAddBtn{ width:100%; height:44px; }

//           .invTableWrap{ display:none; }
//           .invCards{ display:block; }

//           .invFooter{ flex-direction:column; align-items:stretch; }
//           .invFootRight{ align-items:stretch; }
//           .invGrand{ justify-content:space-between; }
//           .invBtn{ width:100%; justify-content:center; }
//           .invActions{ justify-content:stretch; }
//         }
//       `}</style>
//     </div>
//   );
// }

// export default ProvisionalInvoiceModal;